/**
 * Resolver for user-uploaded R2 assets (profile pictures, custom node icons).
 *
 * We store only the R2 object key (e.g. `icon-<userId>-<hash>.svg`,
 * `profile-<userId>-<uuid>.webp`) — never the absolute public URL — so changing
 * the public custom domain is an env-var change (`VITE_R2_PUBLIC_URL`), not a
 * data migration. Legacy rows/localStorage entries that still hold absolute
 * URLs from an older domain are recognized by their key-shaped basename and
 * rewritten here at render time.
 */

/** Matches the key shapes the backend upload routes generate. */
const R2_KEY_RE = /^(icon|profile)-[\w-]+\.[a-z0-9]+$/i;

const R2_PUBLIC_URL: string | undefined = import.meta.env.VITE_R2_PUBLIC_URL;

/**
 * Resolve a stored asset reference to a displayable URL.
 *
 * - bare R2 key → current public URL
 * - http(s) URL whose basename is one of our keys (legacy absolute URLs, any
 *   past domain) → rebuilt against the current public URL
 * - everything else (external pasted image URLs, data:/blob: URIs) → unchanged
 *
 * If `VITE_R2_PUBLIC_URL` is not set, values pass through unchanged.
 */
export function resolveAssetUrl<T extends string | null | undefined>(value: T): T {
  if (!value) return value;
  if (R2_PUBLIC_URL && R2_KEY_RE.test(value)) {
    return `${R2_PUBLIC_URL}/${value}` as T;
  }
  if (R2_PUBLIC_URL && /^https?:\/\//i.test(value)) {
    try {
      const base = new URL(value).pathname.split('/').pop() ?? '';
      if (R2_KEY_RE.test(base)) return `${R2_PUBLIC_URL}/${base}` as T;
    } catch {
      /* not a parseable URL — fall through */
    }
  }
  return value;
}
